'use client';

import { useAuth } from '@clerk/clerk-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Eye,
  FileText,
  GripVertical,
  History,
  MoveDown,
  MoveUp,
  Pencil,
  Plus,
  Save,
  Search,
  Settings2,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, type SelectOption } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { createConfigurationsApi } from '@/features/configurations/api/configurations';
import type {
  AspectKey,
  ConfigSection,
  FieldDefinition,
  FieldType,
  RuntimeAspectSchema,
} from '@/features/configurations/types';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

const ASPECT_KEYS: { key: AspectKey; labelKey: string; descriptionKey: string; icon: LucideIcon }[] = [
  { key: 'assets', labelKey: 'configuration.aspectAssets', descriptionKey: 'configuration.aspectAssetsDesc', icon: Settings2 },
  { key: 'controls', labelKey: 'configuration.aspectControls', descriptionKey: 'configuration.aspectControlsDesc', icon: Settings2 },
  { key: 'vulnerabilities', labelKey: 'configuration.aspectVulnerabilities', descriptionKey: 'configuration.aspectVulnerabilitiesDesc', icon: Settings2 },
  { key: 'threats', labelKey: 'configuration.aspectThreats', descriptionKey: 'configuration.aspectThreatsDesc', icon: Settings2 },
  { key: 'risk', labelKey: 'configuration.aspectRisk', descriptionKey: 'configuration.aspectRiskDesc', icon: Settings2 },
  { key: 'trainings', labelKey: 'configuration.aspectTrainings', descriptionKey: 'configuration.aspectTrainingsDesc', icon: Settings2 },
  { key: 'awareness', labelKey: 'configuration.aspectAwareness', descriptionKey: 'configuration.aspectAwarenessDesc', icon: Settings2 },
];

const SECTION_OPTION_KEYS: { key: ConfigSection; labelKey: string }[] = [
  { key: 'attributes', labelKey: 'configuration.sectionAttributes' },
  { key: 'guidelines', labelKey: 'configuration.sectionGuidelines' },
];

const FIELD_TYPE_VALUES = [
  'text',
  'textarea',
  'number',
  'date',
  'datetime',
  'boolean',
  'select',
  'multiselect',
  'user',
  'role',
  'tag',
  'file',
  'link',
] as const;

function getFieldTypeOptions(t: (key: string) => string): SelectOption[] {
  return FIELD_TYPE_VALUES.map((value) => ({
    value,
    label: t(`configuration.fieldTypes.${value}`),
  }));
}

const OPERATIONAL_LINKS: Record<AspectKey, string | null> = {
  assets: '/assets',
  controls: '/controls',
  vulnerabilities: '/vulnerabilities',
  threats: '/threats',
  risk: '/risks',
  trainings: null,
  awareness: null,
};

const rawSearchSchema = z.object({
  aspect: z
    .enum(['assets', 'controls', 'vulnerabilities', 'threats', 'risk', 'trainings', 'awareness'])
    .optional(),
  section: z.string().optional(),
  module: z.enum(['assets', 'risks', 'vulnerabilities', 'threats', 'controls']).optional(),
  tab: z.enum(['fields', 'guidelines']).optional(),
});

interface RouteSearch {
  aspect: AspectKey;
  section: ConfigSection;
  module?: 'assets' | 'risks' | 'vulnerabilities' | 'threats' | 'controls';
  tab?: 'fields' | 'guidelines';
}

const legacyModuleToAspect: Record<string, AspectKey> = {
  assets: 'assets',
  risks: 'risk',
  vulnerabilities: 'vulnerabilities',
  threats: 'threats',
  controls: 'controls',
};

function normalizeRouteSearch(input: unknown): RouteSearch {
  const parsed = rawSearchSchema.safeParse(input);
  const data = parsed.success ? parsed.data : {};

  const legacyAspect = data.module ? legacyModuleToAspect[data.module] : undefined;
  const aspect = data.aspect ?? legacyAspect ?? 'assets';

  let section: ConfigSection = 'attributes';
  if (data.section === 'attributes' || data.section === 'guidelines') {
    section = data.section;
  }
  if (!data.section && data.tab) {
    section = data.tab === 'fields' ? 'attributes' : 'guidelines';
  }

  const search: RouteSearch = {
    aspect,
    section,
  };

  if (data.module) {
    search.module = data.module;
  }

  if (data.tab) {
    search.tab = data.tab;
  }

  return search;
}

export const Route = createFileRoute('/_authenticated/settings/configration')({
  validateSearch: normalizeRouteSearch,
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.configurations'),
  }),
  component: ConfigurationsPage,
});

interface FieldDraft {
  label: string;
  key: string;
  fieldType: FieldType;
  required: boolean;
  description: string;
  optionsText: string;
  listVisible: boolean;
  filterable: boolean;
  exportable: boolean;
  isHidden: boolean;
}

interface GuidelineDraft {
  title: string;
  description: string;
  docType: 'rich_text' | 'file';
  contentRichtext: string;
  fileUrl: string;
  fileName: string;
  fileMime: string;
  fileSizeBytes: string;
  tagsText: string;
  changeNotes: string;
}

interface RiskScaleItem {
  score: number;
  label: string;
  description?: string;
  probability?: string;
  examples?: string;
}

interface RiskBandItem {
  key: string;
  label: string;
  min: number;
  max: number;
  color: string;
}

interface RiskMatrixDraft {
  formula: string;
  rounding: string;
  likelihoodScale: RiskScaleItem[];
  impactScale: RiskScaleItem[];
  ratingBands: RiskBandItem[];
  manualOverrideAllowed: boolean;
}

function emptyFieldDraft(): FieldDraft {
  return {
    label: '',
    key: '',
    fieldType: 'text',
    required: false,
    description: '',
    optionsText: '',
    listVisible: false,
    filterable: false,
    exportable: false,
    isHidden: false,
  };
}

function emptyGuidelineDraft(): GuidelineDraft {
  return {
    title: '',
    description: '',
    docType: 'rich_text',
    contentRichtext: '',
    fileUrl: '',
    fileName: '',
    fileMime: '',
    fileSizeBytes: '',
    tagsText: '',
    changeNotes: '',
  };
}

function normalizeKey(raw: string): string {
  const normalized = raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  return normalized;
}

function parseOptionsInput(value: string): { valueKey: string; label: string }[] {
  const unique = new Set(
    value
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0)
  );

  return Array.from(unique).map((item) => ({
    valueKey: normalizeKey(item),
    label: item,
  }));
}

function fieldToDraft(field: FieldDefinition): FieldDraft {
  return {
    label: field.label,
    key: field.key,
    fieldType: field.fieldType,
    required: field.required,
    description: field.description ?? '',
    optionsText: field.options.map((option) => option.label).join(', '),
    listVisible: field.ui.listVisible,
    filterable: field.ui.filterable,
    exportable: field.ui.exportable,
    isHidden: field.isHidden,
  };
}

function formatDateTime(value?: string | null): string {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
}

function getRiskMatrixDraft(riskMatrixInput?: unknown): RiskMatrixDraft {
  const riskMatrix =
    riskMatrixInput && typeof riskMatrixInput === 'object'
      ? (riskMatrixInput as Partial<RiskMatrixDraft> & { ratingThresholds?: RiskBandItem[] })
      : ({} as Partial<RiskMatrixDraft> & { ratingThresholds?: RiskBandItem[] });

  const fallbackLikelihood: RiskScaleItem[] = [
    { score: 1, label: i18n.t('settings:configuration.riskLikelihood.rare') },
    { score: 2, label: i18n.t('settings:configuration.riskLikelihood.unlikely') },
    { score: 3, label: i18n.t('settings:configuration.riskLikelihood.possible') },
    { score: 4, label: i18n.t('settings:configuration.riskLikelihood.likely') },
    { score: 5, label: i18n.t('settings:configuration.riskLikelihood.almostCertain') },
  ];

  const fallbackImpact: RiskScaleItem[] = [
    { score: 1, label: i18n.t('settings:configuration.riskImpact.negligible') },
    { score: 2, label: i18n.t('settings:configuration.riskImpact.minor') },
    { score: 3, label: i18n.t('settings:configuration.riskImpact.moderate') },
    { score: 4, label: i18n.t('settings:configuration.riskImpact.major') },
    { score: 5, label: i18n.t('settings:configuration.riskImpact.severe') },
  ];

  const fallbackBands: RiskBandItem[] = [
    { key: 'low', label: i18n.t('settings:configuration.riskBands.low'), min: 1, max: 5, color: '#22c55e' },
    { key: 'medium', label: i18n.t('settings:configuration.riskBands.medium'), min: 6, max: 11, color: '#eab308' },
    { key: 'high', label: i18n.t('settings:configuration.riskBands.high'), min: 12, max: 19, color: '#f97316' },
    { key: 'critical', label: i18n.t('settings:configuration.riskBands.critical'), min: 20, max: 25, color: '#ef4444' },
  ];

  return {
    formula: riskMatrix.formula ?? 'likelihood * impact',
    rounding: riskMatrix.rounding ?? 'none',
    likelihoodScale:
      Array.isArray(riskMatrix.likelihoodScale) && riskMatrix.likelihoodScale.length > 0
        ? riskMatrix.likelihoodScale
        : fallbackLikelihood,
    impactScale:
      Array.isArray(riskMatrix.impactScale) && riskMatrix.impactScale.length > 0
        ? riskMatrix.impactScale
        : fallbackImpact,
    ratingBands:
      Array.isArray(riskMatrix.ratingBands) && riskMatrix.ratingBands.length > 0
        ? riskMatrix.ratingBands
        : Array.isArray(riskMatrix.ratingThresholds) && riskMatrix.ratingThresholds.length > 0
          ? riskMatrix.ratingThresholds
        : fallbackBands,
    manualOverrideAllowed: riskMatrix.manualOverrideAllowed === true,
  };
}

function ConfigurationsPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const { getToken } = useAuth();
  const { isAdmin } = useOrgRole();
  const queryClient = useQueryClient();

  const ASPECTS = React.useMemo(
    () => ASPECT_KEYS.map((a) => ({ key: a.key, label: t(a.labelKey), description: t(a.descriptionKey), icon: a.icon })),
    [t]
  );
  const SECTION_OPTIONS = React.useMemo(
    () => SECTION_OPTION_KEYS.map((s) => ({ key: s.key, label: t(s.labelKey) })),
    [t]
  );
  const FIELD_TYPE_OPTIONS = React.useMemo(() => getFieldTypeOptions(t), [t]);

  const [searchText, setSearchText] = React.useState('');
  const [fieldDialogOpen, setFieldDialogOpen] = React.useState(false);
  const [fieldDialogMode, setFieldDialogMode] = React.useState<'create' | 'edit'>('create');
  const [fieldDraft, setFieldDraft] = React.useState<FieldDraft>(emptyFieldDraft());
  const [editingField, setEditingField] = React.useState<FieldDefinition | null>(null);
  const [deletingField, setDeletingField] = React.useState<FieldDefinition | null>(null);
  const [deletingMode, setDeletingMode] = React.useState<'disable' | 'delete'>('disable');
  const [guidelineDialogOpen, setGuidelineDialogOpen] = React.useState(false);
  const [guidelineDialogMode, setGuidelineDialogMode] = React.useState<'create' | 'version'>('create');
  const [guidelineDraft, setGuidelineDraft] = React.useState<GuidelineDraft>(emptyGuidelineDraft());
  const [guidelineTargetId, setGuidelineTargetId] = React.useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [auditOpen, setAuditOpen] = React.useState(false);
  const [dragFieldId, setDragFieldId] = React.useState<string | null>(null);
  const [isBusy, setIsBusy] = React.useState(false);
  const [riskMatrixDraft, setRiskMatrixDraft] = React.useState<RiskMatrixDraft>(() => getRiskMatrixDraft());

  const api = React.useMemo(() => createConfigurationsApi(getToken), [getToken]);

  const aspectConfigQuery = useQuery({
    queryKey: ['configurations', 'aspect', search.aspect],
    queryFn: () => api.getAspect(search.aspect),
  });

  const runtimePreviewQuery = useQuery({
    queryKey: ['configurations', 'runtime', search.aspect, 'preview'],
    queryFn: () => api.getRuntime(search.aspect, true),
    enabled: previewOpen,
  });

  const auditLogQuery = useQuery({
    queryKey: ['configurations', 'audit', search.aspect],
    queryFn: () => api.getAuditLog(search.aspect),
    enabled: auditOpen,
  });

  const riskMatrixQuery = useQuery({
    queryKey: ['configurations', 'risk', 'matrix'],
    queryFn: () => api.getRiskMatrix(),
    enabled: search.aspect === 'risk',
  });

  React.useEffect(() => {
    if (search.aspect !== 'risk') {
      return;
    }
    const riskMatrix = riskMatrixQuery.data?.riskMatrix;
    if (riskMatrix) {
      setRiskMatrixDraft(getRiskMatrixDraft(riskMatrix));
    }
  }, [riskMatrixQuery.data?.riskMatrix, search.aspect]);

  const aspectConfig = aspectConfigQuery.data;
  const effectiveVersion = aspectConfig?.draft ?? aspectConfig?.published;
  const orderedFields = React.useMemo(
    () =>
      [...(effectiveVersion?.fields ?? [])].sort((left, right) => {
        if (left.ui.displayOrder !== right.ui.displayOrder) {
          return left.ui.displayOrder - right.ui.displayOrder;
        }
        return left.label.localeCompare(right.label);
      }),
    [effectiveVersion?.fields]
  );

  const normalizedSearch = searchText.trim().toLowerCase();
  const visibleFields = React.useMemo(() => {
    if (!normalizedSearch) {
      return orderedFields;
    }

    return orderedFields.filter((field) => {
      const haystack = [field.label, field.key, field.description ?? ''].join(' ').toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [orderedFields, normalizedSearch]);

  const systemFields = visibleFields.filter((field) => field.source === 'system');
  const customFields = visibleFields.filter((field) => field.source === 'custom');

  const refreshAspect = React.useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['configurations', 'aspects'] }),
      queryClient.invalidateQueries({ queryKey: ['configurations', 'aspect', search.aspect] }),
      queryClient.invalidateQueries({ queryKey: ['configurations', 'runtime', search.aspect] }),
      queryClient.invalidateQueries({ queryKey: ['configurations', 'audit', search.aspect] }),
      queryClient.invalidateQueries({ queryKey: ['configurations', 'risk', 'matrix'] }),
    ]);
  }, [queryClient, search.aspect]);

  const selectAspect = (aspect: AspectKey) => {
    void navigate({
      search: {
        aspect,
        section: search.section,
      },
      replace: true,
    });
  };

  const selectSection = (section: ConfigSection) => {
    void navigate({
      search: {
        aspect: search.aspect,
        section,
      },
      replace: true,
    });
  };

  const openCreateField = () => {
    setFieldDialogMode('create');
    setEditingField(null);
    setFieldDraft(emptyFieldDraft());
    setFieldDialogOpen(true);
  };

  const openEditField = (field: FieldDefinition) => {
    setFieldDialogMode('edit');
    setEditingField(field);
    setFieldDraft(fieldToDraft(field));
    setFieldDialogOpen(true);
  };

  const handleSaveField = async () => {
    if (!isAdmin) {
      return;
    }

    if (fieldDraft.label.trim().length === 0) {
      toast.error(t('settings:toast.fieldLabelIsRequired'));
      return;
    }

    if (
      (fieldDraft.fieldType === 'select' || fieldDraft.fieldType === 'multiselect') &&
      parseOptionsInput(fieldDraft.optionsText).length === 0
    ) {
      toast.error(t('settings:toast.selectAndMultiselectFieldsRequireAtLeastOneOption'));
      return;
    }

    const options = parseOptionsInput(fieldDraft.optionsText);

    const payload: Record<string, unknown> = {
      label: fieldDraft.label.trim(),
      fieldType: fieldDraft.fieldType,
      required: fieldDraft.required,
      description: fieldDraft.description.trim() || null,
      ui: {
        displayOrder:
          editingField?.ui.displayOrder ??
          (orderedFields.length > 0
            ? Math.max(...orderedFields.map((field) => field.ui.displayOrder)) + 10
            : 10),
        listVisible: fieldDraft.listVisible,
        filterable: fieldDraft.filterable,
        exportable: fieldDraft.exportable,
      },
      isHidden: fieldDraft.isHidden,
      options,
    };

    if (fieldDialogMode === 'create') {
      payload.key = fieldDraft.key.trim().length > 0 ? normalizeKey(fieldDraft.key) : undefined;
    }

    try {
      setIsBusy(true);
      if (fieldDialogMode === 'create') {
        await api.createField(search.aspect, payload);
        toast.success(t('settings:toast.customFieldCreated'));
      } else if (editingField) {
        await api.updateField(search.aspect, editingField.id, payload);
        toast.success(t('settings:toast.fieldUpdated2'));
      }

      setFieldDialogOpen(false);
      await refreshAspect();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : i18n.t('settings:toast.failedToSaveField'));
    } finally {
      setIsBusy(false);
    }
  };

  const handleDeleteField = async () => {
    if (!isAdmin || !deletingField) {
      return;
    }

    try {
      setIsBusy(true);
      await api.deleteField(search.aspect, deletingField.id, deletingMode);
      toast.success(
        deletingMode === 'disable'
          ? t('configuration.fieldDisabled')
          : t('configuration.fieldPermanentlyDeleted')
      );
      setDeletingField(null);
      await refreshAspect();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : i18n.t('settings:toast.failedToDeleteField'));
    } finally {
      setIsBusy(false);
    }
  };

  const handleReorderFields = async (fieldIds: string[]) => {
    if (!isAdmin) {
      return;
    }

    try {
      setIsBusy(true);
      await api.reorderFields(search.aspect, fieldIds);
      await refreshAspect();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : i18n.t('settings:toast.failedToReorderFields'));
    } finally {
      setIsBusy(false);
    }
  };

  const handleMoveField = async (fieldId: string, direction: 'up' | 'down') => {
    const ids = orderedFields.map((field) => field.id);
    const currentIndex = ids.indexOf(fieldId);
    if (currentIndex < 0) {
      return;
    }

    const nextIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (nextIndex < 0 || nextIndex >= ids.length) {
      return;
    }

    const [item] = ids.splice(currentIndex, 1);
    if (!item) {
      return;
    }
    ids.splice(nextIndex, 0, item);
    await handleReorderFields(ids);
  };

  const handleDropField = async (targetFieldId: string) => {
    if (!dragFieldId || dragFieldId === targetFieldId) {
      return;
    }

    const ids = orderedFields.map((field) => field.id);
    const from = ids.indexOf(dragFieldId);
    const to = ids.indexOf(targetFieldId);
    if (from < 0 || to < 0) {
      return;
    }

    const [item] = ids.splice(from, 1);
    if (!item) {
      return;
    }
    ids.splice(to, 0, item);
    await handleReorderFields(ids);
    setDragFieldId(null);
  };

  const openCreateGuideline = () => {
    setGuidelineDialogMode('create');
    setGuidelineTargetId(null);
    setGuidelineDraft(emptyGuidelineDraft());
    setGuidelineDialogOpen(true);
  };

  const openCreateGuidelineVersion = (guidelineId: string, docType: 'rich_text' | 'file') => {
    setGuidelineDialogMode('version');
    setGuidelineTargetId(guidelineId);
    setGuidelineDraft({
      ...emptyGuidelineDraft(),
      docType,
    });
    setGuidelineDialogOpen(true);
  };

  const handleSaveGuideline = async () => {
    if (!isAdmin) {
      return;
    }

    if (guidelineDialogMode === 'create' && guidelineDraft.title.trim().length === 0) {
      toast.error(t('settings:toast.guidelineTitleIsRequired'));
      return;
    }

    const payload: Record<string, unknown> = {
      description: guidelineDraft.description.trim() || null,
      docType: guidelineDraft.docType,
      contentRichtext: guidelineDraft.docType === 'rich_text' ? guidelineDraft.contentRichtext : null,
      fileUrl: guidelineDraft.docType === 'file' ? guidelineDraft.fileUrl || null : null,
      fileName: guidelineDraft.docType === 'file' ? guidelineDraft.fileName || null : null,
      fileMime: guidelineDraft.docType === 'file' ? guidelineDraft.fileMime || null : null,
      fileSizeBytes:
        guidelineDraft.docType === 'file' && guidelineDraft.fileSizeBytes
          ? Number(guidelineDraft.fileSizeBytes)
          : null,
      changeNotes: guidelineDraft.changeNotes.trim() || null,
    };

    try {
      setIsBusy(true);
      if (guidelineDialogMode === 'create') {
        await api.createGuideline(search.aspect, {
          ...payload,
          title: guidelineDraft.title.trim(),
          tags: guidelineDraft.tagsText
            .split(',')
            .map((item) => item.trim())
            .filter((item) => item.length > 0),
        });
        toast.success(t('settings:toast.guidelineCreated'));
      } else if (guidelineTargetId) {
        await api.createGuidelineVersion(search.aspect, guidelineTargetId, payload);
        toast.success(t('settings:toast.guidelineVersionCreated'));
      }

      setGuidelineDialogOpen(false);
      await refreshAspect();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : i18n.t('settings:toast.failedToSaveGuideline'));
    } finally {
      setIsBusy(false);
    }
  };

  const handleRollbackGuideline = async (guidelineId: string, versionNo: number) => {
    if (!isAdmin) {
      return;
    }

    const confirmed = window.confirm(t('configuration.rollbackConfirm', { versionNo: String(versionNo) }));
    if (!confirmed) {
      return;
    }

    try {
      setIsBusy(true);
      await api.rollbackGuideline(search.aspect, guidelineId, versionNo);
      toast.success(t('settings:toast.guidelineRolledBackToVersion', { versionNo: String(versionNo) }));
      await refreshAspect();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : i18n.t('settings:toast.failedToRollbackGuideline'));
    } finally {
      setIsBusy(false);
    }
  };

  const handleSaveRiskMatrix = async () => {
    if (!isAdmin || search.aspect !== 'risk') {
      return;
    }

    try {
      setIsBusy(true);
      await api.updateRiskMatrix({
        formula: riskMatrixDraft.formula,
        rounding: riskMatrixDraft.rounding,
        likelihoodScale: riskMatrixDraft.likelihoodScale,
        impactScale: riskMatrixDraft.impactScale,
        ratingThresholds: riskMatrixDraft.ratingBands,
        manualOverrideAllowed: riskMatrixDraft.manualOverrideAllowed,
      });
      toast.success(t('settings:toast.riskMatrixUpdated'));
      await refreshAspect();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : i18n.t('settings:toast.failedToSaveRiskMatrix'));
    } finally {
      setIsBusy(false);
    }
  };

  const selectedAspectMeta = ASPECTS.find((item) => item.key === search.aspect);
  if (!selectedAspectMeta) {
    return null;
  }

  const operationalLink = OPERATIONAL_LINKS[search.aspect];
  const runtimePreview = runtimePreviewQuery.data;
  const savePreviewLabel = t('configuration.previewInTab', { label: selectedAspectMeta.label });

  return (
    <div className="flex min-h-[calc(100vh-4rem)] bg-background">
      <aside className="hidden w-72 shrink-0 border-r bg-muted/30 md:block">
        <div className="border-b px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('configuration.aspects')}
          </p>
        </div>
        <ScrollArea className="h-[calc(100vh-7rem)]">
          <div className="space-y-1 p-2">
            {ASPECTS.map((aspect) => {
              const selected = aspect.key === search.aspect;
              const Icon = aspect.icon;
              return (
                <button
                  key={aspect.key}
                  type="button"
                  className={cn(
                    'flex w-full items-start gap-2 rounded-md px-3 py-2 text-left',
                    selected
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  )}
                  onClick={() => {
                    selectAspect(aspect.key);
                  }}
                >
                  <Icon className={cn('mt-0.5 h-4 w-4', selected ? 'text-primary' : 'text-muted-foreground')} />
                  <span>
                    <span className="block text-sm font-medium">{aspect.label}</span>
                    <span className="block text-xs text-muted-foreground">{aspect.description}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </ScrollArea>
      </aside>

      <main className="flex-1 overflow-auto p-6 md:p-8">
        <div className="mx-auto max-w-7xl space-y-6">
          <header className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h1 className="text-3xl font-semibold text-foreground">{t('configuration.pageTitle')}</h1>
                <p className="text-sm text-muted-foreground">
                  {t('configuration.pageDescription')}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={isAdmin ? 'default' : 'secondary'}>
                  {isAdmin ? t('configuration.adminEditable') : t('configuration.readOnly')}
                </Badge>
                <Button variant="outline" onClick={() => {
                  setAuditOpen(true);
                }}>
                  <History className="mr-2 h-4 w-4" />
                  {t('configuration.auditLog')}
                </Button>
                <Button variant="outline" onClick={() => {
                  setPreviewOpen(true);
                }}>
                  <Eye className="mr-2 h-4 w-4" />
                  {savePreviewLabel}
                </Button>
              </div>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">{selectedAspectMeta.label}</CardTitle>
                <CardDescription>{selectedAspectMeta.description}</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap items-center gap-2">
                {operationalLink && (
                  <Button variant="ghost" asChild>
                    <Link to={operationalLink}>{t('configuration.openTab', { label: selectedAspectMeta.label })}</Link>
                  </Button>
                )}
              </CardContent>
            </Card>
          </header>

          <Tabs value={search.section} onValueChange={(value) => {
            selectSection(value as ConfigSection);
          }}>
            <TabsList>
              {SECTION_OPTIONS.map((section) => (
                <TabsTrigger key={section.key} value={section.key}>
                  {section.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="attributes" className="space-y-4">
              <Card>
                <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <CardTitle className="text-base">{t('configuration.attributesTitle')}</CardTitle>
                    <CardDescription>
                      {t('configuration.attributesDescription')}
                    </CardDescription>
                  </div>
                  <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
                    <div className="relative w-full sm:w-72">
                      <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        value={searchText}
                        onChange={(event) => {
                          setSearchText(event.target.value);
                        }}
                        className="pl-9"
                        placeholder={t('configuration.searchFields')}
                      />
                    </div>
                    {isAdmin && (
                      <Button onClick={openCreateField} disabled={isBusy}>
                        <Plus className="mr-2 h-4 w-4" />
                        {t('configuration.addCustomField')}
                      </Button>
                    )}
                  </div>
                </CardHeader>
              </Card>

              <FieldTable
                title={t('configuration.systemFieldsTitle')}
                description={t('configuration.systemFieldsDescription')}
                fields={systemFields}
                isAdmin={isAdmin}
                isBusy={isBusy}
                onEditField={openEditField}
                onDeleteField={(field) => {
                  setDeletingField(field);
                }}
                onDragStart={setDragFieldId}
                onDropField={(fieldId) => {
                  void handleDropField(fieldId);
                }}
                onMoveField={(fieldId, direction) => {
                  void handleMoveField(fieldId, direction);
                }}
              />

              <FieldTable
                title={t('configuration.customFieldsTitle')}
                description={t('configuration.customFieldsDescription')}
                fields={customFields}
                isAdmin={isAdmin}
                isBusy={isBusy}
                onEditField={openEditField}
                onDeleteField={(field) => {
                  setDeletingField(field);
                }}
                onDragStart={setDragFieldId}
                onDropField={(fieldId) => {
                  void handleDropField(fieldId);
                }}
                onMoveField={(fieldId, direction) => {
                  void handleMoveField(fieldId, direction);
                }}
              />

              {search.aspect === 'risk' && (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="text-base">{t('configuration.riskMatrixTitle')}</CardTitle>
                      <CardDescription>
                        {t('configuration.riskMatrixDescription')}
                      </CardDescription>
                    </div>
                    {isAdmin && (
                      <Button onClick={() => {
                        void handleSaveRiskMatrix();
                      }} disabled={isBusy || riskMatrixQuery.isLoading}>
                        <Save className="mr-2 h-4 w-4" />
                        {t('configuration.saveMatrix')}
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-5">
                    {riskMatrixQuery.isLoading ? (
                      <p className="text-sm text-muted-foreground">{t('configuration.loadingRiskMatrix')}</p>
                    ) : riskMatrixQuery.isError ? (
                      <p className="text-sm text-destructive">{t('configuration.failedToLoadRiskMatrix')}</p>
                    ) : (
                      <>
                        <div className="grid gap-3 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label>{t('configuration.formula')}</Label>
                            <Input
                              value={riskMatrixDraft.formula}
                              onChange={(event) => {
                                setRiskMatrixDraft((current) => ({
                                  ...current,
                                  formula: event.target.value,
                                }));
                              }}
                              disabled={!isAdmin || isBusy}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>{t('configuration.rounding')}</Label>
                            <Input
                              value={riskMatrixDraft.rounding}
                              onChange={(event) => {
                                setRiskMatrixDraft((current) => ({
                                  ...current,
                                  rounding: event.target.value,
                                }));
                              }}
                              disabled={!isAdmin || isBusy}
                            />
                          </div>
                        </div>

                        <label className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={riskMatrixDraft.manualOverrideAllowed}
                            disabled={!isAdmin || isBusy}
                            onCheckedChange={(checked) => {
                              setRiskMatrixDraft((current) => ({
                                ...current,
                                manualOverrideAllowed: checked === true,
                              }));
                            }}
                          />
                          {t('configuration.allowManualOverride')}
                        </label>

                        <Separator />

                        <RiskScaleEditor
                          title={t('configuration.likelihoodScale')}
                          items={riskMatrixDraft.likelihoodScale}
                          disabled={!isAdmin || isBusy}
                          onChange={(items) => {
                            setRiskMatrixDraft((current) => ({
                              ...current,
                              likelihoodScale: items,
                            }));
                          }}
                        />

                        <RiskScaleEditor
                          title={t('configuration.impactScale')}
                          items={riskMatrixDraft.impactScale}
                          disabled={!isAdmin || isBusy}
                          onChange={(items) => {
                            setRiskMatrixDraft((current) => ({
                              ...current,
                              impactScale: items,
                            }));
                          }}
                        />

                        <Separator />

                        <RiskBandEditor
                          items={riskMatrixDraft.ratingBands}
                          disabled={!isAdmin || isBusy}
                          onChange={(items) => {
                            setRiskMatrixDraft((current) => ({
                              ...current,
                              ratingBands: items,
                            }));
                          }}
                        />
                      </>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="guidelines" className="space-y-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-base">{t('configuration.guidelinesTitle')}</CardTitle>
                    <CardDescription>
                      {t('configuration.guidelinesDescription')}
                    </CardDescription>
                  </div>
                  {isAdmin && (
                    <Button onClick={openCreateGuideline} disabled={isBusy}>
                      <Plus className="mr-2 h-4 w-4" />
                      {t('configuration.addEntry')}
                    </Button>
                  )}
                </CardHeader>
              </Card>

              {(aspectConfig?.guidelines ?? []).length === 0 ? (
                <Card>
                  <CardContent className="py-10 text-center text-sm text-muted-foreground">
                    {t('configuration.noGuidelineEntries')}
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {(aspectConfig?.guidelines ?? []).map((guideline) => (
                    <Card key={guideline.id}>
                      <CardHeader>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <CardTitle className="text-base">{guideline.title}</CardTitle>
                            <CardDescription>{guideline.description ?? t('configuration.noDescription')}</CardDescription>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{guideline.docType}</Badge>
                            <Badge variant="outline">{t('configuration.versionNumber', { num: String(guideline.currentVersionNo) })}</Badge>
                            {isAdmin && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  openCreateGuidelineVersion(guideline.id, guideline.docType);
                                }}
                              >
                                <FileText className="mr-2 h-4 w-4" />
                                {t('configuration.newVersion')}
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {guideline.versions.length === 0 ? (
                          <p className="text-sm text-muted-foreground">{t('configuration.noVersionsYet')}</p>
                        ) : (
                          guideline.versions.map((version) => (
                            <div
                              key={version.id}
                              className="rounded-md border p-3 text-sm"
                            >
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="font-medium">
                                  {t('configuration.versionNumber', { num: String(version.versionNo) })}
                                </p>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">
                                    {formatDateTime(version.createdAt)}
                                  </span>
                                  {isAdmin && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        void handleRollbackGuideline(guideline.id, version.versionNo);
                                      }}
                                    >
                                      {t('configuration.rollback')}
                                    </Button>
                                  )}
                                </div>
                              </div>
                              {version.changeNotes && (
                                <p className="mt-1 text-xs text-muted-foreground">{version.changeNotes}</p>
                              )}
                              {version.contentRichtext && (
                                <p className="mt-2 whitespace-pre-wrap text-sm">{version.contentRichtext}</p>
                              )}
                              {version.fileName && (
                                <p className="mt-2 text-xs text-muted-foreground">
                                  {t('configuration.fileLabel', { name: version.fileName, type: version.fileMime ?? t('configuration.unknownType') })}
                                </p>
                              )}
                            </div>
                          ))
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

          </Tabs>
        </div>
      </main>

      <Dialog open={fieldDialogOpen} onOpenChange={setFieldDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {fieldDialogMode === 'create' ? t('configuration.createCustomField') : t('configuration.editField')}
            </DialogTitle>
            <DialogDescription>
              {t('configuration.fieldDialogDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>
                  {t('configuration.label')} <span className="text-destructive">*</span>
                </Label>
                <Input
                  value={fieldDraft.label}
                  disabled={
                    isBusy ||
                    (fieldDialogMode === 'edit' &&
                      editingField?.source === 'system' &&
                      !editingField.permissions.canEditLabel)
                  }
                  onChange={(event) => {
                    setFieldDraft((current) => ({
                      ...current,
                      label: event.target.value,
                      key:
                        fieldDialogMode === 'create' && current.key.trim().length === 0
                          ? normalizeKey(event.target.value)
                          : current.key,
                    }));
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('configuration.internalKey')}</Label>
                <Input
                  value={fieldDraft.key}
                  disabled={fieldDialogMode === 'edit' || isBusy}
                  onChange={(event) => {
                    setFieldDraft((current) => ({
                      ...current,
                      key: normalizeKey(event.target.value),
                    }));
                  }}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>{t('configuration.fieldType')}</Label>
                <Select
                  options={FIELD_TYPE_OPTIONS}
                  value={fieldDraft.fieldType}
                  disabled={fieldDialogMode === 'edit' || isBusy}
                  onChange={(value) => {
                    setFieldDraft((current) => ({
                      ...current,
                      fieldType: value as FieldType,
                    }));
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('configuration.description')}</Label>
                <Input
                  value={fieldDraft.description}
                  disabled={
                    isBusy ||
                    (fieldDialogMode === 'edit' &&
                      editingField?.source === 'system' &&
                      !editingField.permissions.canEditDescription)
                  }
                  onChange={(event) => {
                    setFieldDraft((current) => ({
                      ...current,
                      description: event.target.value,
                    }));
                  }}
                />
              </div>
            </div>

            {(fieldDraft.fieldType === 'select' || fieldDraft.fieldType === 'multiselect') && (
              <div className="space-y-2">
                <Label>{t('configuration.optionsCommaSeparated')}</Label>
                <Input
                  value={fieldDraft.optionsText}
                  disabled={
                    isBusy ||
                    (fieldDialogMode === 'edit' &&
                      editingField?.source === 'system' &&
                      !editingField.permissions.canAddOptions)
                  }
                  onChange={(event) => {
                    setFieldDraft((current) => ({
                      ...current,
                      optionsText: event.target.value,
                    }));
                  }}
                  placeholder={t('configuration.optionsPlaceholder')}
                />
              </div>
            )}

            <div className="grid gap-3 md:grid-cols-2">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={fieldDraft.required}
                  disabled={
                    isBusy ||
                    (fieldDialogMode === 'edit' &&
                      editingField?.source === 'system' &&
                      !editingField.permissions.canToggleRequired)
                  }
                  onCheckedChange={(checked) => {
                    setFieldDraft((current) => ({
                      ...current,
                      required: checked === true,
                    }));
                  }}
                />
                {t('configuration.requiredField')}
              </label>

              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={fieldDraft.isHidden}
                  disabled={
                    isBusy ||
                    (fieldDialogMode === 'edit' &&
                      editingField?.source === 'system' &&
                      !editingField.permissions.canHide)
                  }
                  onCheckedChange={(checked) => {
                    setFieldDraft((current) => ({
                      ...current,
                      isHidden: checked === true,
                    }));
                  }}
                />
                {t('configuration.hiddenInForms')}
              </label>

              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={fieldDraft.listVisible}
                  disabled={isBusy}
                  onCheckedChange={(checked) => {
                    setFieldDraft((current) => ({
                      ...current,
                      listVisible: checked === true,
                    }));
                  }}
                />
                {t('configuration.showInListColumns')}
              </label>

              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={fieldDraft.filterable}
                  disabled={isBusy}
                  onCheckedChange={(checked) => {
                    setFieldDraft((current) => ({
                      ...current,
                      filterable: checked === true,
                    }));
                  }}
                />
                {t('configuration.filterable')}
              </label>

              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={fieldDraft.exportable}
                  disabled={isBusy}
                  onCheckedChange={(checked) => {
                    setFieldDraft((current) => ({
                      ...current,
                      exportable: checked === true,
                    }));
                  }}
                />
                {t('configuration.exportable')}
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setFieldDialogOpen(false);
              }}
            >
              {t('configuration.cancel')}
            </Button>
            <Button onClick={() => {
              void handleSaveField();
            }} disabled={isBusy}>
              {fieldDialogMode === 'create' ? t('configuration.createField') : t('configuration.saveChanges')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(deletingField)} onOpenChange={(open) => {
        if (!open) {
          setDeletingField(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('configuration.deleteFieldTitle')}</DialogTitle>
            <DialogDescription>
              {t('configuration.deleteFieldDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={deletingMode === 'disable'}
                onCheckedChange={(checked) => {
                  if (checked === true) {
                    setDeletingMode('disable');
                  }
                }}
              />
              {t('configuration.disableFieldKeepData')}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={deletingMode === 'delete'}
                onCheckedChange={(checked) => {
                  if (checked === true) {
                    setDeletingMode('delete');
                  }
                }}
              />
              {t('configuration.deletePermanently')}
            </label>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeletingField(null);
              }}
            >
              {t('configuration.cancel')}
            </Button>
            <Button variant="destructive" onClick={() => {
              void handleDeleteField();
            }} disabled={isBusy}>
              {t('configuration.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={guidelineDialogOpen} onOpenChange={setGuidelineDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {guidelineDialogMode === 'create' ? t('configuration.addGuidelineEntry') : t('configuration.addGuidelineVersion')}
            </DialogTitle>
            <DialogDescription>
              {t('configuration.guidelineDialogDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {guidelineDialogMode === 'create' && (
              <div className="space-y-2">
                <Label>
                  {t('configuration.title')} <span className="text-destructive">*</span>
                </Label>
                <Input
                  value={guidelineDraft.title}
                  onChange={(event) => {
                    setGuidelineDraft((current) => ({
                      ...current,
                      title: event.target.value,
                    }));
                  }}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>{t('configuration.description')}</Label>
              <Input
                value={guidelineDraft.description}
                onChange={(event) => {
                  setGuidelineDraft((current) => ({
                    ...current,
                    description: event.target.value,
                  }));
                }}
              />
            </div>

            {guidelineDialogMode === 'create' && (
              <div className="space-y-2">
                <Label>{t('configuration.tagsCommaSeparated')}</Label>
                <Input
                  value={guidelineDraft.tagsText}
                  onChange={(event) => {
                    setGuidelineDraft((current) => ({
                      ...current,
                      tagsText: event.target.value,
                    }));
                  }}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>{t('configuration.entryType')}</Label>
              <Select
                options={[
                  { value: 'rich_text', label: t('configuration.richText') },
                  { value: 'file', label: t('configuration.fileMetadata') },
                ]}
                value={guidelineDraft.docType}
                onChange={(value) => {
                  setGuidelineDraft((current) => ({
                    ...current,
                    docType: value as 'rich_text' | 'file',
                  }));
                }}
                disabled={guidelineDialogMode === 'version'}
              />
            </div>

            {guidelineDraft.docType === 'rich_text' ? (
              <div className="space-y-2">
                <Label>{t('configuration.richTextContent')}</Label>
                <Textarea
                  rows={8}
                  value={guidelineDraft.contentRichtext}
                  onChange={(event) => {
                    setGuidelineDraft((current) => ({
                      ...current,
                      contentRichtext: event.target.value,
                    }));
                  }}
                  placeholder={t('configuration.richTextPlaceholder')}
                />
              </div>
            ) : (
              <div className="space-y-3 rounded-md border p-3">
                <div className="space-y-2">
                  <Label>{t('configuration.attachFile')}</Label>
                  <Input
                    type="file"
                    accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (!file) {
                        return;
                      }
                      const objectUrl = URL.createObjectURL(file);
                      setGuidelineDraft((current) => ({
                        ...current,
                        fileName: file.name,
                        fileMime: file.type,
                        fileSizeBytes: String(file.size),
                        fileUrl: objectUrl,
                      }));
                    }}
                  />
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>{t('configuration.fileName')}</Label>
                    <Input
                      value={guidelineDraft.fileName}
                      onChange={(event) => {
                        setGuidelineDraft((current) => ({
                          ...current,
                          fileName: event.target.value,
                        }));
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('configuration.fileMime')}</Label>
                    <Input
                      value={guidelineDraft.fileMime}
                      onChange={(event) => {
                        setGuidelineDraft((current) => ({
                          ...current,
                          fileMime: event.target.value,
                        }));
                      }}
                    />
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>{t('configuration.fileUrl')}</Label>
                    <Input
                      value={guidelineDraft.fileUrl}
                      onChange={(event) => {
                        setGuidelineDraft((current) => ({
                          ...current,
                          fileUrl: event.target.value,
                        }));
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('configuration.fileSize')}</Label>
                    <Input
                      value={guidelineDraft.fileSizeBytes}
                      onChange={(event) => {
                        setGuidelineDraft((current) => ({
                          ...current,
                          fileSizeBytes: event.target.value,
                        }));
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>{t('configuration.changeNotes')}</Label>
              <Input
                value={guidelineDraft.changeNotes}
                onChange={(event) => {
                  setGuidelineDraft((current) => ({
                    ...current,
                    changeNotes: event.target.value,
                  }));
                }}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setGuidelineDialogOpen(false);
              }}
            >
              {t('configuration.cancel')}
            </Button>
            <Button onClick={() => {
              void handleSaveGuideline();
            }} disabled={isBusy}>
              {t('configuration.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{savePreviewLabel}</DialogTitle>
            <DialogDescription>
              {t('configuration.previewDescription')}
            </DialogDescription>
          </DialogHeader>
          {runtimePreviewQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">{t('configuration.loadingPreview')}</p>
          ) : runtimePreviewQuery.isError ? (
            <p className="text-sm text-destructive">{t('configuration.failedToLoadPreview')}</p>
          ) : (
            <FormPreview runtime={runtimePreview} />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('configuration.auditLogTitle')}</DialogTitle>
            <DialogDescription>
              {t('configuration.auditLogDescription')}
            </DialogDescription>
          </DialogHeader>
          {auditLogQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">{t('configuration.loadingAuditEvents')}</p>
          ) : auditLogQuery.isError ? (
            <p className="text-sm text-destructive">{t('configuration.failedToLoadAuditEvents')}</p>
          ) : (
            <ScrollArea className="h-[420px]">
              <div className="space-y-3 pr-3">
                {(auditLogQuery.data?.items ?? []).map((event) => (
                  <div key={event.id} className="rounded-md border p-3 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-medium">{event.action}</p>
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(event.createdAt)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {event.entityType} / {event.entityId}
                    </p>
                  </div>
                ))}
                {(auditLogQuery.data?.items ?? []).length === 0 && (
                  <p className="text-sm text-muted-foreground">{t('configuration.noAuditEvents')}</p>
                )}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FieldTable({
  title,
  description,
  fields,
  isAdmin,
  isBusy,
  onEditField,
  onDeleteField,
  onDragStart,
  onDropField,
  onMoveField,
}: {
  title: string;
  description: string;
  fields: FieldDefinition[];
  isAdmin: boolean;
  isBusy: boolean;
  onEditField: (field: FieldDefinition) => void;
  onDeleteField: (field: FieldDefinition) => void;
  onDragStart: (fieldId: string) => void;
  onDropField: (fieldId: string) => void;
  onMoveField: (fieldId: string, direction: 'up' | 'down') => void;
}) {
  const { t } = useTranslation('settings');
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {fields.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('configuration.noFieldsFound')}</p>
        ) : (
          <div className="space-y-2">
            {fields.map((field) => (
              <div
                key={field.id}
                className="grid gap-2 rounded-md border p-3 md:grid-cols-[auto_1fr_auto]"
                draggable={isAdmin}
                onDragStart={() => {
                  onDragStart(field.id);
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                }}
                onDrop={() => {
                  onDropField(field.id);
                }}
              >
                <div className="flex items-center gap-2 text-muted-foreground">
                  <GripVertical className="h-4 w-4" />
                </div>

                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium">{field.label}</p>
                    {field.required && <Badge variant="secondary">{t('configuration.required')}</Badge>}
                    {field.status !== 'active' && <Badge variant="outline">{t('configuration.disabled')}</Badge>}
                    {field.isHidden && <Badge variant="outline">{t('configuration.hidden')}</Badge>}
                    <Badge variant="outline">{field.source === 'system' ? t('configuration.sourceSystem') : t('configuration.sourceCustom')}</Badge>
                  </div>
                  <p className="font-mono text-xs text-muted-foreground">{field.key}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('configuration.typePrefix')}{field.fieldType}
                    {field.description ? ` • ${field.description}` : ''}
                  </p>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-1">
                  {isAdmin && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          onMoveField(field.id, 'up');
                        }}
                        disabled={isBusy}
                      >
                        <MoveUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          onMoveField(field.id, 'down');
                        }}
                        disabled={isBusy}
                      >
                        <MoveDown className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          onEditField(field);
                        }}
                        disabled={isBusy}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {field.source === 'custom' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            onDeleteField(field);
                          }}
                          disabled={isBusy}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function FormPreview({ runtime }: { runtime: RuntimeAspectSchema | undefined }) {
  const { t } = useTranslation('settings');
  if (!runtime) {
    return <p className="text-sm text-muted-foreground">{t('configuration.noPreviewData')}</p>;
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        {t('configuration.runtimeVersion', { version: runtime.versionNo ? `v${String(runtime.versionNo)}` : t('configuration.draft') })}
      </p>

      {runtime.fields.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('configuration.noFieldsForPreview')}</p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {runtime.fields.map((field) => (
            <div key={field.id} className="space-y-1 rounded-md border p-3">
              <Label>
                {field.label}
                {field.required && <span className="text-destructive"> *</span>}
              </Label>
              <Input disabled placeholder={field.fieldType} />
              {field.description && <p className="text-xs text-muted-foreground">{field.description}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RiskScaleEditor({
  title,
  items,
  disabled,
  onChange,
}: {
  title: string;
  items: RiskScaleItem[];
  disabled: boolean;
  onChange: (items: RiskScaleItem[]) => void;
}) {
  const { t } = useTranslation('settings');
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium">{title}</h3>
      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={`${title}-${String(item.score)}-${String(index)}`} className="grid gap-2 md:grid-cols-3">
            <Input value={String(item.score)} disabled />
            <Input
              value={item.label}
              disabled={disabled}
              onChange={(event) => {
                const next = items.map((entry, currentIndex) =>
                  currentIndex === index ? { ...entry, label: event.target.value } : entry
                );
                onChange(next);
              }}
              placeholder={t('configuration.placeholderLabel')}
            />
            <Input
              value={item.description ?? ''}
              disabled={disabled}
              onChange={(event) => {
                const next = items.map((entry, currentIndex) =>
                  currentIndex === index ? { ...entry, description: event.target.value } : entry
                );
                onChange(next);
              }}
              placeholder={t('configuration.placeholderDescription')}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function RiskBandEditor({
  items,
  disabled,
  onChange,
}: {
  items: RiskBandItem[];
  disabled: boolean;
  onChange: (items: RiskBandItem[]) => void;
}) {
  const { t } = useTranslation('settings');
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">{t('configuration.ratingBands')}</h3>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => {
            onChange([
              ...items,
              {
                key: normalizeKey(`band_${String(items.length + 1)}`),
                label: `${t('configuration.bandPrefix')} ${String(items.length + 1)}`,
                min: 1,
                max: 1,
                color: '#64748b',
              },
            ]);
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t('configuration.addBand')}
        </Button>
      </div>

      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={`${item.key}-${String(index)}`} className="grid gap-2 md:grid-cols-5">
            <Input
              value={item.key}
              disabled={disabled}
              onChange={(event) => {
                const next = items.map((entry, currentIndex) =>
                  currentIndex === index ? { ...entry, key: normalizeKey(event.target.value) } : entry
                );
                onChange(next);
              }}
              placeholder={t('configuration.placeholderKey')}
            />
            <Input
              value={item.label}
              disabled={disabled}
              onChange={(event) => {
                const next = items.map((entry, currentIndex) =>
                  currentIndex === index ? { ...entry, label: event.target.value } : entry
                );
                onChange(next);
              }}
              placeholder={t('configuration.placeholderLabel')}
            />
            <Input
              value={String(item.min)}
              type="number"
              disabled={disabled}
              onChange={(event) => {
                const next = items.map((entry, currentIndex) =>
                  currentIndex === index
                    ? { ...entry, min: Number.parseInt(event.target.value || '0', 10) }
                    : entry
                );
                onChange(next);
              }}
              placeholder={t('configuration.placeholderMin')}
            />
            <Input
              value={String(item.max)}
              type="number"
              disabled={disabled}
              onChange={(event) => {
                const next = items.map((entry, currentIndex) =>
                  currentIndex === index
                    ? { ...entry, max: Number.parseInt(event.target.value || '0', 10) }
                    : entry
                );
                onChange(next);
              }}
              placeholder={t('configuration.placeholderMax')}
            />
            <Input
              value={item.color}
              disabled={disabled}
              onChange={(event) => {
                const next = items.map((entry, currentIndex) =>
                  currentIndex === index ? { ...entry, color: event.target.value } : entry
                );
                onChange(next);
              }}
              placeholder="#22c55e"
            />
          </div>
        ))}
      </div>
    </div>
  );
}
